import { useState, useEffect } from 'react';
import { useLocalization } from 'hooks/useLocalization';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogTitle, DialogContent, Box, Button, Alert, Stack } from '@mui/material';
import CartItemList from 'components/activity-display/control-header/CartItemList';
import CloseButton from "components/shared/CloseButton";
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { useActivityShopUI } from 'hooks/useActivityShopUI';
import { useBusiness } from 'hooks/useBusiness';
import ActivityCard from 'components/activity-display/layouts/ActivityCard';

interface CartDialogProps {
	cartIsOpen: boolean;
	setCartIsOpen: (open: boolean) => void;
}

function CartDialog({ cartIsOpen, setCartIsOpen }: CartDialogProps) {
	const [showRecommendations, setShowRecommendations] = useState<boolean>(false);
	const [hasInteracted, setHasInteracted] = useState<boolean>(false);
	const { getItemsInCartQty, activitiesInteractionData, selectedCurrencyCode, getActivityInteractionData } = useActivityShopUI();
	const { getStr } = useLocalization();
	const { activities, activityRecommendationsCleaned } = useBusiness();
	const navigate = useNavigate();

	const itemsInCartQty = getItemsInCartQty;

	useEffect(() => {
		if (cartIsOpen && !hasInteracted) {
			setHasInteracted(true);
		}
	}, [cartIsOpen]);

	//Check that recommendation activities actually still exist

	const checkRecommendations = () => {
		const activitiesInCart = activitiesInteractionData.filter(activityInteractionData => activityInteractionData.isAdded);
		const recommendedActivityIds: number[] = activityRecommendationsCleaned.filter(activityRecommendation => {
			const isAdded = activitiesInCart.some(activityInCart => activityInCart.activityId === activityRecommendation.activityId);
			const shouldRecommend = activityRecommendation.recommendWhenActivityIds.added.every(id =>
				activitiesInCart.some(activityInCart => activityInCart.activityId === id)) &&
				activityRecommendation.recommendWhenActivityIds.notAdded.every(id =>
					!activitiesInCart.some(activityInCart => activityInCart.activityId === id)
				);

			return !isAdded && shouldRecommend;
		}).map(activityRecommendation => activityRecommendation.activityId);

		if (recommendedActivityIds.length === 0) {
			handleGoToCheckout();
		} else {
			setShowRecommendations(true);
		}
	};

	useEffect(() => {
		if (hasInteracted && showRecommendations) {
			const activitiesInCart = activitiesInteractionData.filter(activity => activity.isAdded);
			const allRecommendationsAdded = activityRecommendationsCleaned.every(recommendation => {
				return activitiesInCart.some(activity => activity.activityId === recommendation.activityId);
			});
	
			if (allRecommendationsAdded) {
				handleGoToCheckout();
			}
		}
	}, [activitiesInteractionData, activityRecommendationsCleaned, navigate]);

	const handleGoToCheckout = () => {
		navigate('/' + getStr('PUBLIC_APP_URL_checkout'));
	};
	
	return(
		<>
			<Dialog
				open={cartIsOpen}
				onClose={() => setCartIsOpen(false)}
				PaperProps={{
					style: {
						width: '100%',
						maxWidth: '400px', // Use a specific pixel or percentage value
					},
				}}
			>
				<DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Box flex="1" textAlign="left">{getStr('Cart')}</Box>
					<CloseButton handleClose={() => setCartIsOpen(false)} />
				</DialogTitle>
				<DialogContent dividers>
					<CartItemList
						isInteractive={true}
						onEmpty={() => setCartIsOpen(false)}
						activitiesInteractionData={activitiesInteractionData}
						selectedCurrencyCode={selectedCurrencyCode}
					/>
					{
						itemsInCartQty() > 0 ? (
							<Button
								variant="contained"
								color="primary"
								fullWidth
								onClick={checkRecommendations}
								sx={{ marginTop: '20px' }}
								startIcon={<ShoppingCartCheckoutIcon />}
								endIcon={<KeyboardDoubleArrowRightIcon />}
							>
								{getStr('Go_to_checkout')}
							</Button>
						) : null
					}
				</DialogContent>
			</Dialog>
			<Dialog
				open={showRecommendations}
				onClose={() => setShowRecommendations(false)}
			>
				<DialogTitle>
					{getStr('Recommended_activities')}
				</DialogTitle>
				<DialogContent
					sx={{
						backgroundColor: 'rgba(0, 0, 0, 0.04)',
					}}
					dividers
				>
					<Alert severity="warning">
						{getStr('RECOMMENDED_ACTIVITIES_CTA_INFO')}
					</Alert>
					<Stack
						spacing={3}
						sx={{ marginTop: '20px' }}
					>
						{
							activityRecommendationsCleaned.map((activityRecommendation) => {
								const recommendedActivity = activities.find(activity => activity.id === activityRecommendation.activityId);

								if (!recommendedActivity) return null;
								
								const activityInteractionData = getActivityInteractionData(recommendedActivity.id);
								
								if (!recommendedActivity || !activityInteractionData || activityInteractionData.isAdded) return null;

								return (
									<ActivityCard key={recommendedActivity.id} activity={recommendedActivity} />
								);
							})
						}
						<Button
							variant="contained"
							color="error"
							fullWidth
							onClick={handleGoToCheckout}
							sx={{ marginTop: '20px' }}
							startIcon={<ShoppingCartCheckoutIcon />}
							endIcon={<KeyboardDoubleArrowRightIcon />}
						>
							{getStr('Continue_without_recommended_activities')}
						</Button>
					</Stack>
				</DialogContent>
			</Dialog>
		</>
	);
}

export default CartDialog;